import { create } from 'zustand';
import type { Model } from '../types';
import { captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';

interface ModelState {
  models: Model[];
  selectedIds: Set<string>;
  isLoading: boolean;
  loadModels: (maxAgeMs?: number) => Promise<boolean>;
  addModel: (filePath: string, metadata: Partial<Model>) => Promise<Model>;
  updateModel: (id: string, updates: Partial<Model>) => Promise<void>;
  deleteModel: (id: string) => Promise<void>;
  toggleSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  reset: () => void;
}

let modelSequence = 0;
let modelEpoch = 0;

let lastModelLoad: { at: number; epoch: ReturnType<typeof captureModelEpoch> } | undefined;
let activeModelLoad: { epoch: ReturnType<typeof captureModelEpoch>; promise: Promise<boolean> } | undefined;

function captureModelEpoch() {
  return { store: modelEpoch, session: captureSessionEpoch() };
}

function isModelEpochCurrent(epoch: ReturnType<typeof captureModelEpoch>): boolean {
  return epoch.store === modelEpoch && isSessionEpochCurrent(epoch.session);
}

export const useModelStore = create<ModelState>((set) => ({
  models: [],
  selectedIds: new Set(),
  isLoading: false,

  loadModels: async (maxAgeMs = 0) => {
    if (maxAgeMs > 0 && lastModelLoad && isModelEpochCurrent(lastModelLoad.epoch)
      && Date.now() - lastModelLoad.at < maxAgeMs) return true;
    if (maxAgeMs > 0 && activeModelLoad && isModelEpochCurrent(activeModelLoad.epoch)) return activeModelLoad.promise;
    const epoch = captureModelEpoch();
    const requestSequence = ++modelSequence;
    const promise = (async () => {
      set({ isLoading: true });
      try {
        const models = await window.electron.invoke(window.IPC_CHANNELS.MODEL_LIST);
        if (requestSequence !== modelSequence || !isModelEpochCurrent(epoch)) return false;
        lastModelLoad = { at: Date.now(), epoch };
        set({ models, isLoading: false });
        return true;
      } catch (error) {
        console.error('Failed to load models:', error);
        if (requestSequence === modelSequence && isModelEpochCurrent(epoch)) set({ isLoading: false });
        return false;
      }
    })();
    const entry = { epoch, promise };
    activeModelLoad = entry;
    try { return await promise; }
    finally { if (activeModelLoad === entry) activeModelLoad = undefined; }
  },

  addModel: async (filePath, metadata) => {
    const epoch = captureModelEpoch();
    const model = await window.electron.invoke(window.IPC_CHANNELS.MODEL_CREATE, {
      filePath,
      metadata,
    });
    if (isModelEpochCurrent(epoch)) set((state) => ({ models: [model, ...state.models] }));
    return model;
  },

  updateModel: async (id, updates) => {
    const epoch = captureModelEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.MODEL_UPDATE, { id, updates });
    if (!isModelEpochCurrent(epoch)) return;
    set((state) => ({
      models: state.models.map((m) => (m.id === id ? { ...m, ...updates } : m)),
    }));
  },

  deleteModel: async (id) => {
    const epoch = captureModelEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.MODEL_DELETE, { id });
    if (!isModelEpochCurrent(epoch)) return;
    set((state) => ({
      models: state.models.filter((m) => m.id !== id),
      selectedIds: new Set([...state.selectedIds].filter((sid) => sid !== id)),
    }));
  },

  toggleSelection: (id) =>
    set((state) => {
      const newSet = new Set(state.selectedIds);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return { selectedIds: newSet };
    }),

  selectAll: () =>
    set((state) => ({
      selectedIds: new Set(state.models.map((m) => m.id)),
    })),

  clearSelection: () => set({ selectedIds: new Set() }),

  reset: () => {
    lastModelLoad = undefined;
    activeModelLoad = undefined;
    ++modelEpoch;
    ++modelSequence;
    set({ models: [], selectedIds: new Set(), isLoading: false });
  },
}));
